/**
 * /locations/:state/:region — Regional hub.
 *
 * For the Feb 2026 SEO pilot this page powers the Sunshine Coast
 * regional hub, which lists active PWS Partners covering the region,
 * a suburb grid grouped by cluster, and the mail-out fallback.
 *
 * Bookings originating from this page are attributed to the region's
 * active partner via the existing `?p=<slug>` link — falling back to
 * standard region_allocation when the user browses to /book directly.
 */
import { useEffect, useState } from "react";
import { Navigate, Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Building2, Send, Droplets, FlaskConical, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SchemaJsonLd, breadcrumbSchema } from "@/lib/seo";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { STATES, REGIONS, suburb_short_slug_of } from "@/data/locationTree";
import { LOCATIONS, isDraft } from "@/lib/locationContent";
import { suburbs_for_cluster } from "@/data/regionSuburbs";
import { hero_for_region } from "@/data/regionHeroes";
import { cover_for_cluster } from "@/data/clusterCovers";
import api from "@/lib/api";

const BASE_URL = SEO_DEFAULTS.baseUrl;

export default function RegionHub() {
  const { state: stateSlug, region: regionSlug } = useParams();
  const state  = STATES[stateSlug];
  const region = REGIONS[regionSlug];
  const url = `${BASE_URL}/locations/${stateSlug}/${regionSlug}`;

  const seoTitle = region && state
    ? `${region.name} Water Testing — ${state.name}`
    : "Water Testing";
  const seoDesc = region
    ? `Independent, laboratory-certified water testing across the ${region.name} region. ${region.tagline}`
    : undefined;

  useSEO({
    title: seoTitle, description: seoDesc, url,
    noindex: !region || region.state !== stateSlug,
  });

  // Feb 2026 — Partner resolution.
  //
  // We fetch active partners two ways and merge (dedupe by
  // partner_slug):
  //   1. `by-region` — attribute-based discovery. Any partner tagged
  //      in Admin with matching `state` + `franchise_region` appears
  //      here automatically. This is the primary path in prod.
  //   2. `partner_slugs[]` in `locationTree.js` — legacy hardcoded
  //      list, retained as a safety fallback for edge cases where an
  //      admin curates the region's partner set outside the attribute
  //      match (e.g. cross-region service arrangements).
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!region || !state) return;
      const merged = new Map();  // partner_slug → row

      // (1) By-region discovery — query-param form (region names can
      //     contain "/" which breaks the path-segment route).
      try {
        const { data } = await api.get("/pws-partner-landing/by-region", {
          params: { state: state.abbr, region: region.name },
        });
        for (const row of (data?.rows || [])) {
          if (row?.partner_slug) merged.set(row.partner_slug, row);
        }
      } catch { /* silent — the fallback path below still runs */ }

      // (2) Hardcoded slug fallback
      const legacySlugs = (region.partner_slugs || []).filter(
        (s) => !merged.has(s),
      );
      if (legacySlugs.length > 0) {
        try {
          const items = await Promise.all(
            legacySlugs.map(async (pslug) => {
              try {
                const { data } = await api.get(`/pws-partner-landing/by-partner-slug/${pslug}`);
                return { ...data, partner_slug: data.partner_slug || pslug };
              } catch {
                return null;
              }
            }),
          );
          for (const row of items) {
            if (row?.partner_slug) merged.set(row.partner_slug, row);
          }
        } catch { /* silent — page still renders */ }
      }

      if (!cancelled) setPartners(Array.from(merged.values()));
    })();
    return () => { cancelled = true; };
  }, [region, state]);

  if (!region || !state || region.state !== stateSlug) {
    return <Navigate to="/locations/australia" replace />;
  }

  // Suburb rows grouped by cluster.
  //
  // Two-source display model:
  //   • Published landing pages live in `locations.json` — those
  //     become clickable tiles that route to /locations/:state/:region/:short.
  //   • Additional coverage suburbs live in `regionSuburbs.js` — those
  //     render as plain chips (display-only, no landing page).
  // A cluster row appears if EITHER source has entries.
  const clusterRows = region.clusters.map((c) => {
    const published = LOCATIONS
      .filter((l) => l.region_cluster === c.key && !isDraft(l))
      .sort((a, b) => a.suburb_name.localeCompare(b.suburb_name));
    const publishedNames = new Set(published.map((p) => p.suburb_name.toLowerCase()));
    const rosterOnly = suburbs_for_cluster(c.key).filter(
      (name) => !publishedNames.has(name.toLowerCase()),
    );
    return { ...c, published, rosterOnly };
  }).filter((row) => row.published.length > 0 || row.rosterOnly.length > 0);

  const totalSuburbs = clusterRows.reduce(
    (sum, r) => sum + r.published.length + r.rosterOnly.length,
    0,
  );

  const heroImage = hero_for_region(region.slug);

  // Every other live region in the same state, for the cross-link block.
  const siblingRegions = Object.values(REGIONS)
    .filter((r) => r.state === stateSlug && r.slug !== region.slug)
    .sort((a, b) => a.name.localeCompare(b.name));

  const breadcrumbs = [
    { name: "Home", url: BASE_URL },
    { name: "Locations", url: `${BASE_URL}/locations` },
    { name: "Australia", url: `${BASE_URL}/locations/australia` },
    { name: state.name, url: `${BASE_URL}/locations/${state.slug}` },
    { name: region.name, url },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`region-hub-${region.slug}`}>
      <Header />
      <SchemaJsonLd data={breadcrumbSchema(breadcrumbs)} />

      {/* Hero */}
      <section className="relative border-b border-slate-100 overflow-hidden">
        {heroImage && (
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <img
              src={heroImage.src}
              alt={heroImage.alt}
              className="w-full h-full object-cover"
              data-testid="region-hub-hero-image"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40" />
          </div>
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-14 md:py-20">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/locations" className="hover:text-slate-900">Locations</Link>
            <span className="mx-2">/</span>
            <Link to="/locations/australia" className="hover:text-slate-900">Australia</Link>
            <span className="mx-2">/</span>
            <Link to={`/locations/${state.slug}`} className="hover:text-slate-900">{state.name}</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-900 font-medium">{region.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-3 py-1 text-xs font-semibold mb-4">
            <MapPin className="w-3.5 h-3.5" /> {region.name} · {state.abbr} · Postcodes {region.postcodes_covered_range}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900" data-testid="region-hub-h1">
            Water testing across the {region.name}.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">{region.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={partners[0]?.partner_slug ? `/book-water-test?p=${partners[0].partner_slug}` : "/book-water-test"}>
              <Button className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6" data-testid="region-hub-book-cta">
                Book a {region.name} water test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/mail-out-water-testing-kits">
              <Button variant="outline" className="rounded-full h-11 px-6" data-testid="region-hub-mailout-cta">
                <Send className="w-4 h-4 mr-2" /> Post your sample instead
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Active partners */}
      {partners.length > 0 && (
        <section id="local-partners" className="border-b border-slate-100 bg-slate-50 scroll-mt-24">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <div className="flex items-center gap-2 mb-6">
              <Users className="w-4 h-4 text-slate-500" />
              <h2 className="text-base font-semibold text-slate-800">Local PWS Partners covering this region</h2>
              <span className="ml-auto text-xs text-slate-500">
                Bookings from this page route to the attributed partner automatically.
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partners.map((p) => (
                <div key={p.partner_slug} className="rounded-2xl bg-white border border-slate-200 p-6 flex items-start gap-4" data-testid={`region-hub-partner-${p.partner_slug}`}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shrink-0">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-bold text-slate-900">{p.business_name}</div>
                    {p.region_label && (
                      <div className="text-sm text-slate-600 mt-0.5">{p.region_label}</div>
                    )}
                    {p.service_suburbs && p.service_suburbs.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Service areas</div>
                        <div className="flex flex-wrap gap-1">
                          {p.service_suburbs.slice(0, 12).map((sa, i) => (
                            <span key={i} className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{sa}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex gap-2">
                      {/* Storefront routes resolve by the landing-page
                          `slug` (e.g. riverina-southern-tablelands), NOT
                          the partner attribution slug (PWS-RIVERINA) —
                          the `?p=` booking link below is the one that
                          uses partner_slug. */}
                      <Link to={`/pws-partner/${p.slug || p.partner_slug}`}>
                        <Button variant="outline" className="rounded-full h-9 text-xs" data-testid={`region-hub-partner-profile-${p.partner_slug}`}>
                          Partner profile
                        </Button>
                      </Link>
                      <Link to={`/book-water-test?p=${p.partner_slug}`}>
                        <Button className="rounded-full bg-slate-900 hover:bg-slate-800 text-white h-9 text-xs" data-testid={`region-hub-partner-book-${p.partner_slug}`}>
                          Book with {(p.business_name || "this partner").split(" ")[0]} <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Water sources */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Droplets className="w-4 h-4 text-cyan-700" /> Water sources across the {region.name}
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {region.water_sources.map((s) => (
              <div key={s.key} className="rounded-2xl border border-slate-200 p-5" data-testid={`region-hub-source-${s.key}`}>
                <div className="text-sm font-bold text-slate-900">{s.label}</div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">{s.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suburb grid by cluster */}
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-slate-800">Suburbs we test in</h2>
            <div className="text-xs text-slate-500" data-testid="region-hub-suburb-count">
              {totalSuburbs} suburbs · Postcodes {region.postcodes_covered_range}
            </div>
          </div>
          <div className="mt-8 space-y-8">
            {clusterRows.map((c) => {
              const clusterTotal = c.published.length + c.rosterOnly.length;
              const cover = cover_for_cluster(c.key);
              return (
              <div key={c.key} data-testid={`region-hub-cluster-${c.key}`}>
                {cover && (
                  <div className="mb-4 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100" data-testid={`region-hub-cluster-cover-${c.key}`}>
                    <img
                      src={cover.src}
                      alt={cover.alt}
                      className="w-full h-32 md:h-40 object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-900">{c.name}</h3>
                  <div className="text-xs text-slate-500">{clusterTotal} suburb{clusterTotal === 1 ? "" : "s"}</div>
                </div>
                <p className="text-sm text-slate-600 mb-4">{c.summary}</p>
                {c.published.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                    {c.published.map((s) => {
                      const short = suburb_short_slug_of(s.slug);
                      return (
                        <Link
                          key={s.slug}
                          to={`/locations/${state.slug}/${region.slug}/${short}`}
                          className="rounded-xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-sm transition p-3 flex items-center justify-between"
                          data-testid={`region-hub-suburb-${short}`}
                        >
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{s.suburb_name}</div>
                            <div className="text-[11px] text-slate-500">Postcode {s.postcode}</div>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                        </Link>
                      );
                    })}
                  </div>
                )}
                {c.rosterOnly.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid={`region-hub-cluster-roster-${c.key}`}>
                    {c.rosterOnly.map((name) => (
                      <span
                        key={name}
                        className="inline-flex text-[11px] font-medium rounded-full bg-white border border-slate-200 text-slate-700 px-2.5 py-1"
                        data-testid={`region-hub-suburb-chip-${name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sibling regions — Sep 2026 internal-linking fix.
          A region whose suburbs have no published landing pages picked
          up only 2 inbound links (state hub + locations hub), leaving it
          effectively orphaned. Cross-linking siblings within the state
          gives every region hub a healthy inbound count and gives
          visitors the obvious "not my region?" next step. */}
      {siblingRegions.length > 0 && (
        <section className="border-b border-slate-100 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <h2 className="text-base font-semibold text-slate-800 mb-1">
              Other regions we cover in {state.name}
            </h2>
            <p className="text-sm text-slate-600 mb-6">
              Not the {region.name}? Jump straight to your region.
            </p>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              data-testid="region-hub-sibling-regions"
            >
              {siblingRegions.map((r) => (
                <Link
                  key={r.slug}
                  to={`/locations/${state.slug}/${r.slug}`}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-sm transition p-4 flex items-start justify-between gap-3"
                  data-testid={`region-hub-sibling-${r.slug}`}
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {r.name} water testing
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {r.tagline}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 mt-0.5 shrink-0 group-hover:text-cyan-600 transition-colors" />
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <Link
                to={`/locations/${state.slug}`}
                className="text-sm font-semibold text-cyan-700 hover:text-cyan-800"
                data-testid="region-hub-state-hub-link"
              >
                See all {state.name} water testing locations →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Trust footer */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <FlaskConical className="w-5 h-5 text-cyan-700" />
            <div className="mt-2 font-bold text-slate-900">Independent Testing Laboratory</div>
            <p className="text-slate-600 mt-1">Physical, chemical, and microbial parameters analysed to ADWG.</p>
          </div>
          <div>
            <Users className="w-5 h-5 text-cyan-700" />
            <div className="mt-2 font-bold text-slate-900">Local Partner support</div>
            <p className="text-slate-600 mt-1">Bookings within this region route to a nominated PWS Partner for follow-up quotes on any recommendations.</p>
          </div>
          <div>
            <Send className="w-5 h-5 text-cyan-700" />
            <div className="mt-2 font-bold text-slate-900">Mail-out fallback</div>
            <p className="text-slate-600 mt-1">Outside a partner service area? Post your sample and we&#39;ll test it exactly the same way.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
